/* eslint-disable @typescript-eslint/no-explicit-any */
import CategorySort from '@/components/Category/CategorySort'
import ImageOptimize from '@/components/ImageOptimize'
import ProductCard from '@/components/ProductCard'
import ProductCardSkeleton from '@/components/Skeleton/ProductCardSkeleton'
import { PageType, ProductCardType } from '@/constants'
import { useGetProductsByCategory } from '@/hooks/useProductHook'
import HeaderSearch from '@/layouts/HeaderSearch'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import { type Product, type ProductResponse, type QueryParam } from '@/types'
import { setContext } from '@/utils/api-interceptors'
import {
  getBanner,
  getMenus,
  getProductWithPaginate,
  getProvinces,
  getQuickMenu,
  getSaleRegion,
} from '@/utils/endpoint'
import { useSearchParams } from '@/utils/next-util'
import { getCookies } from 'cookies-next'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { v4 as uuidv4 } from 'uuid'
export const sortTypes = [
  { label: 'Giá tăng dần', value: 'priceDesc' },
  { label: 'Giá giảm dần', value: 'priceAsc' },
  { label: 'Mới nhất', value: 'dateDesc' },
  { label: 'Bán chạy nhất', value: 'dateAsc' },
  { label: 'Tên: A – Z', value: 'alphaAsc' },
  { label: 'Tên: Z – A', value: 'alphaDesc' },
]
export const sortByTypes = [
  // { label: 'Nên mua', type: 'custom', name: 'shouldBuy' },
  { label: 'Bán chạy', type: 'custom', name: 'bestSelling' },
  { label: 'Giá thấp > cao', type: 'sortType', name: 'asc' },
  { label: 'Giá cao > thấp', type: 'sortType', name: 'desc' },
]
const SearchPage = ({
  productSearchData,
}: {
  productSearchData: ProductResponse
}) => {
  const router = useRouter()
  const [, setTotalProduct] = useState<number>(productSearchData?.total || 0)
  const [products, setProducts] = useState<Product[]>(productSearchData?.data)
  const { getProducts } = useGetProductsByCategory()
  const [totalPages, setTotalPages] = useState(productSearchData?.pages)
  const [currentPage, setCurrentPage] = useState(1)
  const [routerChange, setRouterChange] = useState(false)
  const [loading, setLoading] = useState(false)
  const [currentParams, setCurrentParams] = useSearchParams([
    {
      name: 'page',
      value: null,
    },
    {
      name: 'query',
      value: null,
    },
    {
      name: 'sortBy',
      value: null,
    },
    {
      name: 'sortType',
      value: null,
    },
    {
      name: 'bestSelling',
      value: null,
    },
    {
      name: 'shouldBuy',
      value: null,
    },
    {
      name: 'allowInstallment',
      value: null,
    },
  ])
  useEffect(() => {
    if (productSearchData?.data) {
      setProducts(productSearchData?.data)
      setTotalProduct(productSearchData?.total)
    }
    return () => {
      setCurrentPage(1)
    }
  }, [productSearchData])
  const [sortByOptions] = useState(sortByTypes)
  const activeParams = currentParams
  const activeSort = activeParams.find(
    (filter: any) => filter.name === 'sortType'
  ).value
  const activeSortCustom = activeParams
    .filter((filter: any) => filter.value === 'true')
    .map((item: { name: string }) => item.name) || ['']

  const updateParams = useCallback(
    (params: any, routerChange = true) => {
      setLoading(true)
      setCurrentPage(1)
      setProducts([])
      if (routerChange) {
        setRouterChange(true)
      }
      const newFilters = activeParams.map((filter: any) => {
        const matchedParam = params?.find((p: any) => p.name === filter.name)
        return matchedParam ? { ...filter, value: matchedParam?.value } : filter
      })
      setCurrentParams(newFilters)
    },
    [activeParams]
  )

  useEffect(() => {
    if (!routerChange) {
      return
    }
    setLoading(true)
    getProducts(
      {
        limit: 12,
        currentPage: currentPage,
        sortBy: (router?.query.sortBy as string) || 'score',
        sortType: (router?.query.sortType as string) || 'desc',
        search: router?.query?.query,
      },
      {
        onSuccess: res => {
          setProducts(res.data.data)
          setLoading(false)
          setTotalPages(res.data.pages)
          setTotalProduct(res.data.total)
        },
      }
    )
  }, [router.query])
  const fetchMoreProducts = () => {
    setCurrentPage(currentPage + 1)
    setLoading(true)
    getProducts(
      {
        limit: 12,
        currentPage: currentPage + 1,
        sortBy: (router?.query.sortBy as string) || 'score',
        sortType: (router?.query.sortType as string) || 'desc',
        search: router?.query?.query,
      },
      {
        onSuccess: res => {
          setProducts(products.concat(res.data.data))
          setLoading(false)
          setTotalPages(res.data.pages)
        },
      }
    )
  }

  return (
    <Main
      showRecentlyViewed
      meta={<Meta noIndex noFollow title={'Tìm kiếm'} description={''} />}
      pageLayout={PageType.SEARCH}
      bgColor='#FFFFFF'
    >
      <HeaderSearch />
      <section className='px-2 w-full pb-4 min-h-[calc(100vh-76px)]'>
        <h3 className='mb-2.5 mt-4 font-bold text-base'>
          Kết quả tìm kiếm{' '}
          <span className='font-normal'>“{router?.query?.query}”</span>
        </h3>
        {products && products.length > 0 && (
          <div className='mb-4 mt-1.5'>
            <CategorySort
              sortOptions={sortByOptions}
              onChange={updateParams}
              activeSort={activeSort}
              activeSortCustom={activeSortCustom}
            />
          </div>
        )}
        {loading && (
          <div className='products grid grid-cols-2 gap-3 pb-[120px]'>
            {[...new Array(8)].map(() => (
              <ProductCardSkeleton key={uuidv4()} />
            ))}
          </div>
        )}
        {products && products.length > 0 ? (
          <InfiniteScroll
            className='pb-[30px]'
            dataLength={products.length}
            next={fetchMoreProducts}
            hasMore={
              currentPage !== Number(totalPages) || Number(totalPages) === 0
            }
            loader=''
          >
            <div className='productData grid grid-cols-2 gap-3'>
              {products.map((product: Product, idx: number) => (
                <ProductCard
                  layout={2}
                  product={product}
                  key={product?._id + idx}
                  type={
                    product.flashSale
                      ? ProductCardType.FLASH_SALE
                      : ProductCardType.PRODUCT_CARD
                  }
                />
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          ''
        )}
        {products && products.length == 0 && !loading && (
          <div className='mt-[100px] h-full flex flex-col items-center justify-center'>
            <ImageOptimize
              src='/assets/images/no-product.svg'
              alt={'Không tìm thấy sản phẩm phù hợp với bạn'}
              width={124}
              height={145}
            />
            <span className='mt-[20px] w-[200px] text-center font-normal'>
              Không tìm thấy sản phẩm tương ứng
            </span>
          </div>
        )}
      </section>
    </Main>
  )
}

export default SearchPage

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res, query } = context
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )

  const params = {
    ...query,
    page: 1,
    fullTextSearch: query?.query,
    sortBy: query?.sortBy ? query?.sortBy : 'score',
    searching: true,
    sortType: query?.sortType ? query?.sortType : 'desc',
    limit: 12,
  }
  const [stores, menus, banner, quickMenu, productSearchData, regions] =
    await Promise.all([
      getProvinces(),
      getMenus(),
      getBanner(),
      getQuickMenu(),
      getProductWithPaginate(params as QueryParam),
      getSaleRegion(),
    ])

  return {
    props: {
      stores,
      menus,
      banner,
      language,
      messages,
      quickMenu,
      productSearchData,
      regions,
      token: token || null,
    },
  }
}
